import React, { useContext, useMemo } from 'react';
import { StaticImage } from 'gatsby-plugin-image';
import { DeviceType } from '@/utils/definitions';
import { ChangeTarget } from '@/features/editor/widgets/changelog/change-target-button';
import { ElementTracking } from '@/webapi/use-experience-api';
import { LightningSvg } from '@/features/editor/widgets/changelog/lightning-icon';
import { Controls, Icon, Ordinal, Title, Wrapper } from '../shared/shared';
import { SelectorTooltipWrapper } from '@/features/editor/widgets/changelog/tile';
import { TrackEventTooltip } from '@/features/editor/widgets/changelog/event-tracking/event-track-tooltip';
import { EditorContext } from '@/features/editor/context/editor-context';
import { ChangeVariant } from '@/features/editor/widgets/changelog/event-tracking/change-variant-button';
import { shouldSync } from '@/utils/experience-utils';

type Props = {
  ordinal: number;
  elemTrack: ElementTracking;

  device: DeviceType;

  onClick?: () => void;
  onHover?: () => void;
};

export function ChangelogEventTile({
  ordinal,
  device,
  onClick,
  onHover,
  elemTrack,
}: Props) {
  const { experienceState } = useContext(EditorContext);
  const currentExperience = experienceState?.currentExperience;
  const activeVariant = experienceState?.getCurrentVariant();
  const handleChangePlacement = (placement) => {
    const targetPlacement = {
      kind: placement,
      other: currentExperience?.targeting?.placement?.other,
    };
    experienceState.setElementTracking({
      ...elemTrack,
      targetPlacement,
    } as any);
  };
  const isSynced = useMemo(
    () =>
      shouldSync(currentExperience, elemTrack) &&
      (elemTrack.variant === activeVariant.id ||
        [undefined, `Control`].includes(elemTrack.variant)),
    [currentExperience, activeVariant],
  );

  return (
    <Wrapper
      isSynced={isSynced}
      onMouseEnter={onHover}
      onClick={() => {
        isSynced && onClick?.();
      }}
      device={device}
    >
      <SelectorTooltipWrapper />
      <Ordinal device={device}>{ordinal}</Ordinal>
      <Title device={device}>
        Collect click data for {` `}
        <strong>{elemTrack.name}</strong>
        {` `}
        <ChangeTarget
          isSynced
          change={elemTrack as any}
          handleSelect={handleChangePlacement}
        />
        {` `}
        <ChangeVariant elemTrack={elemTrack as any} />
      </Title>
      <Controls>
        <LightningSvg />
        <Icon data-tip data-for={`track-${elemTrack.ordinal}`}>
          <StaticImage
            height={20}
            placeholder="none"
            loading="eager"
            src="../../../../../assets/three_dots_2.svg"
            alt="close"
          />
        </Icon>
      </Controls>
      <TrackEventTooltip elmTrack={elemTrack as any} />
    </Wrapper>
  );
}
